import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Sale } from "../models/sale.model";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5001/api/";

// console.log(BASE_URL)

const safeWh = (wh: any) => (wh && wh !== "undefined" && wh !== "null" ? wh : "allWh");

export const SaleApi = createApi({
  reducerPath: "SaleApi",
  baseQuery: fetchBaseQuery({ baseUrl: BASE_URL }),
  tagTypes: ["Sale"],
  endpoints: (builder) => ({
    Sales: builder.query<Sale[], void>({
      query: () => `/sale`,
      providesTags: ["Sale"],
    }),
    SalesCount: builder.query<Sale[], any>({
      query: ({ aamarId }) => `/sale/count/${aamarId}`,
      providesTags: ["Sale"],
    }),
    SalesPointSpent: builder.query<Sale[], any>({
      query: ({ startDate, endDate }) =>
        `/sale/todayPoint/${startDate}/${endDate}`,
      providesTags: ["Sale"],
    }),
    SalesWeekly: builder.query<Sale, any>({
      query: ({ warehouse, aamarId }) =>
        `/sale/week-sale/${safeWh(warehouse)}/${aamarId}`,
      providesTags: ["Sale"],
    }),
    Sale: builder.query<Sale, string>({
      query: (_id) => `/sale/${_id}`,
      providesTags: ["Sale"],
    }),
    LastSale: builder.query<Sale, any>({
      query: ({ warehouse, aamarId }) =>
        `/sale/lastSale/${safeWh(warehouse)}/${aamarId}`,
      providesTags: ["Sale"],
    }),
    SaleByInvoice: builder.query<Sale, string>({
      query: (invoiceId) => `/sale/invoice/${invoiceId}`,
      providesTags: ["Sale"],
    }),
    SaleByDate: builder.query<Sale[], any>({
      query: ({ startDate, endDate, warehouse, aamarId, q }) =>
        `/sale/byDate/${startDate}/${endDate}/${safeWh(warehouse)}/${aamarId}?q=${q || ""}`,
      providesTags: ["Sale"],
    }),
    DashboardSaleView: builder.query<Sale[], any>({
      query: ({startDate, endDate,warehouse, aamarId }) =>
        `/sale/dashboardSaleView/${startDate}/${endDate}/${safeWh(warehouse)}/${aamarId}`,
      providesTags: ["Sale"],
    }),
    dueSale: builder.query<Sale[], any>({
      query: ({ warehouse, aamarId }) =>
        `/sale/dueSale/${safeWh(warehouse)}/${aamarId}`,
      providesTags: ["Sale"],
    }),
    SaleByDateAfterSale: builder.query<Sale[], any>({
      query: ({ startDate, endDate, supplier }) =>
        `/sale/aftersale/${startDate}/${endDate}/${supplier}`,
      providesTags: ["Sale"],
    }),
    SaleTotal: builder.query<Sale[], any>({
      query: ({ startDate, endDate }) => `/sale/total/${startDate}/${endDate}`, // not found
      providesTags: ["Sale"],
    }),
    dashboardSale: builder.query<Sale[], any>({
      query: ({ startDate, endDate, warehouse, aamarId }) =>
        `/sale/dashboardSale/${startDate}/${endDate}/${safeWh(warehouse)}/${aamarId}`,
      providesTags: ["Sale"],
    }),
    dueCalculation: builder.query<Sale[], any>({
      query: ({ startDate, endDate, warehouse, aamarId }) =>
        `/sale/dueCalculation/${startDate}/${endDate}/${safeWh(warehouse)}/${aamarId}`,
      providesTags: ["Sale"],
    }),
    SaleFootfall: builder.query<Sale[], any>({
      query: ({ startDate, endDate, warehouse, aamarId }) =>
        `/sale/footfall/${startDate}/${endDate}/${safeWh(warehouse)}/${aamarId}`,
      providesTags: ["Sale"],
    }),
    SaleExportByDate: builder.query<Sale[], any>({
      query: ({ startDate, endDate }) => `/sale/export/${startDate}/${endDate}`,
      providesTags: ["Sale"],
    }),
    SaleExportByDateAndCat: builder.query<Sale[], any>({
      query: ({ startDate, endDate, warehouse, cat, aamarId }) =>
        `/sale/byCategory/${startDate}/${endDate}/${safeWh(warehouse)}/${aamarId}/${cat}`,
      providesTags: ["Sale"],
    }),
    SaleExportByDateAndSupplier: builder.query<Sale[], any>({
      query: ({ startDate, endDate, supplier }) =>
        `/sale/bySupplier/${startDate}/${endDate}/${supplier}`,
      providesTags: ["Sale"],
    }),
    SaleByBiller: builder.query<Sale[], any>({
      query: ({ startDate, endDate, biller }) =>
        `/sale/posSale/${startDate}/${endDate}/${biller}`,
      providesTags: ["Sale"],
    }),
    DelSaleExportByDate: builder.query<Sale[], any>({
      query: ({ startDate, endDate }) =>
        `/sale/exportDel/${startDate}/${endDate}`,
      providesTags: ["Sale"],
    }),

    CustomerPurchaseHistory: builder.query<Sale[], any>({
      query: ({ start, end, id }) =>
        `/sale/customerHistory/${start}/${end}/${id}`,
      providesTags: ["Sale"],
    }),
    GetCustomerById: builder.query<Sale[], any>({
      query: (id) => `/sale/getCustomerById/${id}`,
      providesTags: ["Sale"],
    }),
    SaleArticelExportByDate: builder.query<Sale[], any>({
      query: ({ startDate, endDate, warehouse, aamarId }) =>
        `/sale/exportArticale/${startDate}/${endDate}/${safeWh(warehouse)}/${aamarId}`,
      providesTags: ["Sale"],
    }),
    SaleCategoryByDate: builder.query<Sale[], any>({
      query: ({ startDate, endDate, warehouse, aamarId }) =>
        `/sale/category/${startDate}/${endDate}/${safeWh(warehouse)}/${aamarId}`,
      providesTags: ["Sale"],
    }),

    addSale: builder.mutation<{}, Sale>({
      query: (Sale) => ({
        url: "/sale",
        method: "POST",
        body: Sale,
      }),
      invalidatesTags: ["Sale"],
    }),
    updateSale: builder.mutation<void, Sale>({
      query: ({ _id, ...rest }) => ({
        url: `/sale/${_id}`,
        method: "PUT",
        body: rest,
      }),
      invalidatesTags: ["Sale"],
    }),
    // deleteSale: builder.mutation<void, string>({
    //     query: (id) => ({
    //         url: `/sale/${ id }`,
    //         method: 'DELETE',
    //     }),
    //     invalidatesTags: ['Sale']
    // }),
    deleteTempSale: builder.mutation<void, Sale>({
      query: ({ _id, ...rest }) => ({
        url: `/sale/${_id}`,
        method: "PUT",
        body: rest,
      }),
      invalidatesTags: ["Sale"],
    }),
    lossProfitTotal: builder.query<Sale[], any>({
      query: ({ startDate, endDate, warehouse, aamarId }) =>
        `/sale/lossProfitTotal/${startDate}/${endDate}/${safeWh(warehouse)}/${aamarId}`,
      providesTags: ["Sale"],
    }),
  }),
});

export const {
  useSalesQuery,
  useSalesCountQuery,
  useSalesPointSpentQuery,
  useSalesWeeklyQuery,
  useSaleQuery,
  useLastSaleQuery,
  useSaleByDateQuery,
  useGetCustomerByIdQuery,
  useSaleByDateAfterSaleQuery,
  useSaleTotalQuery,
  useDueSaleQuery,
  useDashboardSaleQuery,
  useDueCalculationQuery,
  useDashboardSaleViewQuery,
  useSaleFootfallQuery,
  useSaleByInvoiceQuery,
  useSaleExportByDateQuery,
  useSaleExportByDateAndCatQuery,
  useSaleExportByDateAndSupplierQuery,
  useSaleByBillerQuery,
  useDelSaleExportByDateQuery,
  useSaleArticelExportByDateQuery,
  useSaleCategoryByDateQuery,
  useAddSaleMutation,
  useDeleteTempSaleMutation,
  useUpdateSaleMutation,
  // useDeleteSaleMutation,
  useCustomerPurchaseHistoryQuery,
  useLossProfitTotalQuery,
} = SaleApi;

export default SaleApi;
